import React, { useEffect, useMemo, useState } from "react";
import Navigation from "../components/Navigation";
import PortfolioGraph from "../components/PortfolioGraph";
import { TOP_US_STOCKS, getTopUsStock } from "../data/topUsStocks";
import "./TestScreen.css";

const SliderControl = ({ id, label, value, min, max, step, unit, onChange }) => (
  <div className="test-playground__slider">
    <div className="test-playground__slider-head">
      <label htmlFor={id}>{label}</label>
      <span className="test-playground__slider-value">
        {value}
        {unit}
      </span>
    </div>
    <input
      id={id}
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(event) => onChange(Number(event.target.value))}
    />
  </div>
);

const TestScreen = () => {
  const [graphHeight, setGraphHeight] = useState(500);
  const [graphWidth, setGraphWidth] = useState(100);
  const [gridRows, setGridRows] = useState(1);
  const [gridColumns, setGridColumns] = useState(1);
  const [graphSymbols, setGraphSymbols] = useState(["AAPL"]);

  const graphCount = gridRows * gridColumns;
  const graphMargin = useMemo(
    () => Math.max(12, Math.round(graphHeight / 28)),
    [graphHeight]
  );

  const graphSlots = useMemo(
    () => Array.from({ length: graphCount }, (_, index) => index),
    [graphCount]
  );

  useEffect(() => {
    setGraphSymbols((previous) => {
      if (previous.length === graphCount) return previous;
      if (previous.length < graphCount) {
        const next = [...previous];
        for (let index = previous.length; index < graphCount; index += 1) {
          next.push(TOP_US_STOCKS[index % TOP_US_STOCKS.length].symbol);
        }
        return next;
      }
      return previous.slice(0, graphCount);
    });
  }, [graphCount]);

  const updateGraphSymbol = (slot, symbol) => {
    setGraphSymbols((previous) => {
      const next = [...previous];
      next[slot] = symbol;
      return next;
    });
  };

  return (
    <div className="screen test-screen">
      <Navigation />

      <div className="test-playground">
        <header className="test-playground__header">
          <h1>PortfolioGraph Playground</h1>
          <p>Compare up to 20 large US stocks with live Twelve Data API charts</p>
        </header>

        <div className="test-playground__toolbar card">
          <div className="test-playground__controls">
            <div className="test-playground__sliders">
              <SliderControl
                id="graph-height"
                label="Height"
                value={graphHeight}
                min={300}
                max={900}
                step={10}
                unit="px"
                onChange={setGraphHeight}
              />
              <SliderControl
                id="graph-width"
                label="Width"
                value={graphWidth}
                min={40}
                max={100}
                step={5}
                unit="%"
                onChange={setGraphWidth}
              />
              <SliderControl
                id="grid-rows"
                label="Rows"
                value={gridRows}
                min={1}
                max={4}
                step={1}
                unit=""
                onChange={setGridRows}
              />
              <SliderControl
                id="grid-columns"
                label="Columns"
                value={gridColumns}
                min={1}
                max={4}
                step={1}
                unit=""
                onChange={setGridColumns}
              />
            </div>

            <div className="test-playground__stocks">
              <h3 className="test-playground__stocks-title">Stocks</h3>
              <div className="test-playground__stocks-grid">
                {graphSlots.map((slot) => {
                  const symbol = graphSymbols[slot] || TOP_US_STOCKS[0].symbol;
                  return (
                    <div key={slot} className="test-playground__stock-picker">
                      <label htmlFor={`stock-${slot}`}>
                        {graphCount === 1 ? "Stock" : `Graph ${slot + 1}`}
                      </label>
                      <select
                        id={`stock-${slot}`}
                        className="test-playground__stock-select"
                        value={symbol}
                        onChange={(event) =>
                          updateGraphSymbol(slot, event.target.value)
                        }
                      >
                        {TOP_US_STOCKS.map((option) => (
                          <option key={option.symbol} value={option.symbol}>
                            {option.symbol} — {option.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="test-playground__info" aria-live="polite">
            <h3>Info</h3>
            <dl>
              <div>
                <dt>Graph size</dt>
                <dd>
                  {graphWidth}% × {graphHeight}px
                </dd>
              </div>
              <div>
                <dt>Grid layout</dt>
                <dd>
                  {gridRows} row{gridRows !== 1 ? "s" : ""} × {gridColumns}{" "}
                  column{gridColumns !== 1 ? "s" : ""}
                </dd>
              </div>
              <div>
                <dt>Graphs shown</dt>
                <dd>{graphCount}</dd>
              </div>
              <div>
                <dt>Chart margin</dt>
                <dd>{graphMargin}px</dd>
              </div>
              <div>
                <dt>Data source</dt>
                <dd>Twelve Data API</dd>
              </div>
            </dl>
          </div>
        </div>

        <div
          className="test-playground__preview-wrap"
          style={{ width: `${graphWidth}%` }}
        >
          <section
            className="test-playground__preview"
            style={{
              gridTemplateRows: `repeat(${gridRows}, auto)`,
              gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))`,
            }}
          >
            {graphSlots.map((slot) => {
              const symbol = graphSymbols[slot] || TOP_US_STOCKS[0].symbol;
              const stock = getTopUsStock(symbol);

              return (
                <div key={slot} className="test-playground__cell">
                  <PortfolioGraph
                    symbol={symbol}
                    title={stock.name}
                    height={graphHeight}
                    width="100%"
                    margin={graphMargin}
                    showTimeframes={true}
                    showTitle={true}
                    showPrice={true}
                    showBackground={true}
                    showDisplayMode={true}
                  />
                </div>
              );
            })}
          </section>
        </div>
      </div>
    </div>
  );
};

export default TestScreen;
